import type { IdeaCard } from "./types";

export const SOURCES: Record<string, string> = {
  dipas: "DIPAS",
  tempo30: "Tempo 30",
};
export const DECISIONS: Record<string, string> = {
  werkstatt: "Werkstatt",
  spaeter: "Später",
  verworfen: "Verworfen",
  gemerkt: "Gemerkt",
};
export const KINDS: Record<string, string> = {
  antrag: "Antrag",
  anfrage_24: "Anfrage §24",
  anfrage_27: "Anfrage §27",
  empfehlung_27: "Empfehlung §27",
};
export const DOT_CLASS: Record<string, string> = {
  gruen: "bg-success",
  gelb: "bg-warning",
  rot: "bg-destructive",
};
/** The dot shows whether the idea is new and still open, not the score. */
export const DOT_TEXT: Record<string, string> = {
  gruen: "neu und offen",
  gelb: "prüfen",
  rot: "schon befasst oder erledigt",
};
export const HANDLED: Record<string, string> = {
  neu: "keine Drucksache",
  teilweise: "teilweise befasst",
  befasst: "schon befasst",
  unklar: "unklar",
};
export const DONE: Record<string, string> = {
  offen: "noch offen",
  teilweise: "teilweise umgesetzt",
  erledigt: "erledigt",
  unklar: "unklar",
};
/** Older cards only carry the overlap check. */
const LEGACY: Record<string, string> = {
  gruen: "keine",
  gelb: "möglich, prüfen",
  rot: "schon befasst",
};

export const dotText = (
  k: Pick<IdeaCard, "ampel" | "dopplung" | "score_details">,
) => {
  if (k.score_details?.raster !== "v2")
    return `Dopplung: ${LEGACY[k.ampel ?? ""] ?? "nicht geprüft"}`;
  return `BV: ${HANDLED[k.dopplung?.urteil ?? ""] ?? "unklar"} · Umsetzung: ${DONE[k.score_details.erledigt?.urteil ?? ""] ?? "unklar"}`;
};

export const SCORE_WEIGHTS = [
  ["Qualität", 35],
  ["Aktualität", 20],
  ["Zuständigkeit", 20],
  ["Volt-Passung", 15],
  ["Zustimmung", 10],
] as const;

/** Creation date of the source contribution, for the meta line. */
export const createdText = (k: IdeaCard) => {
  const d =
    typeof k.fakten?.erstellt_am === "string"
      ? new Date(k.fakten.erstellt_am)
      : null;
  return d && !isNaN(d.getTime())
    ? `vom ${d.toLocaleDateString("de-DE")}`
    : null;
};

export const shortDate = (t: string) => new Date(t).toLocaleDateString("de-DE");

/** Papers behind an overlap verdict, the ones the model cited first. */
export const similarPapers = (k: IdeaCard) => {
  const d = k.dopplung;
  const show =
    k.score_details?.raster === "v2"
      ? d?.urteil === "befasst" || d?.urteil === "teilweise"
      : k.ampel === "gelb" || k.ampel === "rot";
  if (!show) return [];
  const cited = new Set(d?.nummern ?? []);
  return [...(d?.treffer ?? [])].sort(
    (a, b) => Number(cited.has(b.nummer)) - Number(cited.has(a.nummer)),
  );
};

// URL parameter (English, in the CRM hash) → API parameter.
export const IDEA_FILTERS = [
  { param: "source", api: "typ", label: "Quelle" },
  { param: "decision", api: "entscheidung", label: "Entscheidung" },
  { param: "status", api: "status", label: "Status" },
  { param: "committee", api: "ausschuss", label: "Ausschuss" },
  { param: "district", api: "stadtteil", label: "Stadtteil" },
] as const;
export const SEARCH_PARAM = "q";
export const SORT_PARAM = "sortBy";
export const BUNDLE_PARAM = "bundle";
/** Without a decision filter the list opens on "offen"; "all" lifts it. */
export const ALL_DECISIONS = "all";
export const NO_COMMITTEE = "-";

/** API request parameters from the page parameters. */
export const ideaQuery = (
  values: Record<string, string>,
): Record<string, string> => {
  const q: Record<string, string> = {};
  for (const f of IDEA_FILTERS) {
    const v = values[f.param];
    if (f.param === "decision") {
      if (!v) q.entscheidung = "offen";
      else if (v !== ALL_DECISIONS) q.entscheidung = v;
    } else if (v) q[f.api] = v;
  }
  const search = values[SEARCH_PARAM]?.trim();
  if (search) q.q = search;
  if (values[SORT_PARAM]) q.sort = values[SORT_PARAM];
  if (values[BUNDLE_PARAM]) q.buendel = values[BUNDLE_PARAM];
  return q;
};

/** Whether a loaded card still matches the filters; the next page starts after the matching ones. */
export const stillMatches = (k: IdeaCard, q: Record<string, string>) => {
  const e = q.entscheidung;
  const ok = !e
    ? true
    : e === "gemerkt"
      ? k.gemerkt
      : k.entscheidung === e && !(e === "offen" && k.gemerkt);
  return ok && (!q.status || k.status === q.status);
};

export const valueLabel = (api: string, value: string) =>
  api === "typ"
    ? (SOURCES[value] ?? value)
    : api === "entscheidung" && DECISIONS[value]
      ? DECISIONS[value]
      : api === "ausschuss" && value === NO_COMMITTEE
        ? "Ohne Ausschuss"
        : value;
